import type { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { api } from './api.js';

import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlDialog } from '@shoelace-style/shoelace';
import { docLinkStyles, pageStyles } from './components/cssstyles.js';
import { ProjectContext } from './components/project-context.js';
import { consume, contexts, FKProjectChanged } from './contexts.js';
import { createMalformedReportsDataProvider, createReportsDataProvider } from './data-table-providers/runsProvider.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { projectPlatformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { tasks } from './tasks.js';

@customElement('page-project-runs')
export class PageProjectRuns extends LitElement {

  static url(options: { orgSlug: string, projectSlug: string, snapshotId: WireTypes.DataSnapshotId | undefined }): string {
    return projectPlatformUrl(['', options.orgSlug, options.projectSlug, 'runs'].join('/'), undefined, options.snapshotId);
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/:project/runs',
      ],
      render: (groups) => ProjectContext.render({
        orgSlug: groups?.org,
        projectSlug: groups?.project,
      }, html`
        <page-project-runs
          .orgSlug=${groups?.org}
          .projectSlug=${groups?.project}
        ></page-project-runs>
      `),
    }];
  }

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.projectReferences, subscribe: true }) private _branches?: ContextType<typeof contexts.projectReferences>;
  @consume({ context: contexts.user }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;

  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) orgSlug?: string;

  @query('sl-dialog') private _dialog?: SlDialog;

  private _dataTableProviderTask = new Task(this, {
    args: () => [
      this._project, this._snapshotId,
    ] as const,
    task: async ([project, snapshotId], { signal }) => {
      CHECK(project && snapshotId);
      return createReportsDataProvider(project, snapshotId);
    }
  });

  private _unparsedReportsDataProviderTask = new Task(this, {
    args: () => [
      this._project, this._snapshotId,
    ] as const,
    task: async ([project, snapshotId], { signal }) => {
      CHECK(project && snapshotId);
      return createMalformedReportsDataProvider({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
      });
    }
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug, this.projectSlug] as const,
    metadata: ([orgSlug, projectSlug]) => orgSlug && projectSlug ? pageMetadata({
      orgSlug, projectSlug, section: 'Runs',
    }) : undefined,
  });

  render() {
    if (!this._project)
      return nothing;

    const unparedReports = this._project.snapshot?.malformedRunsCount ?? 0;

    return html`
      <sl-dialog label="Malformed Reports" style="--width: 75vw;">
        <data-table
          .pageNumber=${0}
          .pageSize=${20}
          .provider=${this._unparsedReportsDataProviderTask.value}
          .noDataMessage=${'No Malformed Reports'}
        ></data-table>
      </sl-dialog>

      <project-header
        .orgSlug=${this.orgSlug}
        .projectSlug=${this.projectSlug}
        .submenu=${"runs"}
      ></project-header>
      <project-body wide user-role=${this._project.access}>
        ${unparedReports > 0 ? html`
          <fk-callout variant=warn>
            <v-box>
              <div>${unparedReports} malformed report${unparedReports > 1 ? 's' : ''} could not be processed.
              ${this._project.access === 'editor' ? html`
                Once you've identified and fixed the issue, delete them to keep your project tidy.
                ` : nothing}
              </div>
              <h-box>
                <sl-button @click=${() => this._dialog?.show() } size=small>View Malformed Reports</sl-button>
                ${this._project.access === 'editor' ? html`
                  <sl-button size=small variant=danger @click=${async () => {
                    if (!this._project)
                      return;
                    if (!confirm('Are you sure you want to delete all malformed reports?'))
                      return;
                    await api.history.deleteAllMalformedReports.POST({
                      orgSlug: this._project.org.orgSlug,
                      projectSlug: this._project.projectSlug,
                    });
                    FKProjectChanged.dispatch(this, this._project);
                  }}>Delete all malformed</sl-button>
                ` : nothing}
                </h-box>
              </v-box>
          </fk-callout>
        ` : nothing}
        <data-table
          .pageNumber=${0}
          .pageSize=${20}
          .provider=${this._dataTableProviderTask.value}
          .noDataMessage=${'No Runs'}
        ></data-table>  

      </project-body>
      <app-footer></app-footer>
    `
  }

  static styles = [githubMarkdownCSS, docLinkStyles, pageStyles, css`
    data-table {
      --gap: var(--sl-spacing-x-large);
    }
  `];
}
